import React, { useEffect, useRef, useState } from 'react';
import { translate } from '../../services/translate';
import alertAudioFile from './../../../assets/alert.mp3';
import { eventsService } from '../../services/central-api/events';
import { dispatchService } from '../../services/central-api/dispatch';
import { EventParamList } from '../../typings/Params';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import Filters from './Filters';
import useFilters from '../../components/Filter/hooks/useFilters';
import { ClientError } from '../../services/central-api/base-service';
import Toast from 'react-native-toast-message';
import { useAuth } from '../../context/Auth';
import { useQuery } from '@tanstack/react-query';
import { ViewToken } from 'react-native';
import Container from '../../components/Container';
import Header from '../../components/Header';
import Kanban from '../../components/Kanban';
import PlateImage from '../../components/lpr/PlateImage';
import { useStyles } from 'react-native-unistyles';
import { MyImage } from '../../components/MyImage';
import personDefaultPicture from '../../../assets/person-default-picture.png';

const sound = new Audio(alertAudioFile);

export default function EventsBoard({ navigation }: { navigation: Navigation; }) {
    const { theme } = useStyles();
    const { user: authenticatedUser } = useAuth();
    const route = useRoute<RouteProp<EventParamList, 'EventsBoard'>>();
    const [filters, setFilters] = useFilters<EventParamList['EventsBoard']>({ ...route.params });
    const [playSound, setPlaySound] = useState<{ existingEvents: number[]; newEvents: number[]; }>({ existingEvents: [], newEvents: [] });

    useEffect(() => {
        if (playSound.newEvents.length === 0) {
            return;
        }

        if (playSound.newEvents.some(item => !playSound.existingEvents.includes(item))) {
            console.log('sound alert');
            playAlertSound();
        }
    }, [playSound]);

    const isFocused = useIsFocused();
    const { data, refetch } = useQuery({
        queryKey: ['getEvents', filters],
        queryFn: () => eventsService.getEvents(filters).then(async (result) => {

            if (data) {
                const existingEvents = data.responseList.filter(ev => ev.situation === 'pending').map(ev => ev.id);
                const newEvents = result.responseList.filter(ev => ev.situation === 'pending').map(ev => ev.id);

                setPlaySound({ existingEvents, newEvents });
            }

            return result;
        }),
        enabled: isFocused,
        refetchInterval: (data) => {
            if (!isFocused) {
                return false;
            }
            if (!data?.refreshInterval) {
                return false;
            }

            return data.refreshInterval;
        },
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: true,
    });

    function playAlertSound() {
        sound.currentTime = 0;
        sound.play().catch(err => console.error(err));
    }

    async function goToEvent(item: OccurrenceEvent) {
        try {
            if (item.situation == 'pending' && item.id) {
                await dispatchService.setOccurrenceInService(item.id);
            }
            navigation.navigate('Events', { screen: 'EventsOccurrenceDetail', params: { occurrenceId: item.id } });
        } catch (err) {
            if ((err instanceof ClientError)) {
                refetch();
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });

        }
    }

    const [, updateState] = React.useState({});
    const forceUpdate = React.useCallback(() => updateState({}), []);
    const [images,] = useState<{ [key: string]: { blobUrl: string; fetched: boolean; platePosition?: { x: number; y: number; }[]; }; }>({});

    const flatListCallback = useRef(({ viewableItems }: { viewableItems: ViewToken[]; }) => {
        viewableItems.map(viewableItem => {
            const occurrenceEvent: OccurrenceEvent = viewableItem.item.content;
            if (!occurrenceEvent.details?.imageUrl) {
                return;
            }

            if (occurrenceEvent.triggerType == 'face_detected'
                || occurrenceEvent.triggerType == 'missing_face_detected'
                || occurrenceEvent.triggerType == 'mp_face_detected'
                || occurrenceEvent.triggerType == 'illegal_debris') {
                return;
            }

            if ((occurrenceEvent.triggerType == 'plate_detected'
                || occurrenceEvent.triggerType == 'plate_detected_by_mobile_agent'
                || occurrenceEvent.triggerType == 'irregular_vehicle'
                || occurrenceEvent.triggerType == 'clone_suspect'
                || occurrenceEvent.triggerType == 'cortex_plate_detected')
                && !occurrenceEvent.details?.platePosition) {
                return;
            }
            const hash = getHashKeyForImage(occurrenceEvent.details.imageUrl);
            if (viewableItem.isViewable && !images[hash].fetched) {
                images[hash] = {
                    blobUrl: '',
                    fetched: true,
                    platePosition: occurrenceEvent.details?.platePosition,
                };
                fetchImageAsBlob(occurrenceEvent.details.imageUrl, hash);
            }
        });
    }).current;

    async function fetchImageAsBlob(url: string, hash: string) {
        try {
            const res = await fetch(url);
            const blob = await res.blob();
            images[hash] = {
                blobUrl: URL.createObjectURL(blob),
                fetched: true,
                platePosition: images[hash].platePosition
            };
            forceUpdate();
        } catch (err) {
            return '';
        }
    }

    useEffect(() => {
        fetchImages(data?.responseList ?? []);
    }, [data]);

    async function fetchImages(occurrences: OccurrenceEvent[]) {
        for (const occurrence of occurrences) {
            if (occurrence.details?.imageUrl && images[getHashKeyForImage(occurrence.details.imageUrl)] === undefined) {
                images[getHashKeyForImage(occurrence.details.imageUrl)] = {
                    blobUrl: '',
                    fetched: false,
                    platePosition: occurrence.details.platePosition
                };
            }
        }

        forceUpdate();
    }

    function getHashKeyForImage(url: string) {
        const imageUrl = new URL(url);
        return imageUrl.host + imageUrl.pathname;
    }

    function isFace(occurrence: OccurrenceEvent) {
        return (occurrence.triggerType == 'face_detected'
            || occurrence.triggerType == 'missing_face_detected'
            || occurrence.triggerType == 'mp_face_detected');
    }

    function getImage(occurrence: OccurrenceEvent) {
        if (isFace(occurrence)) {
            return <img
                style={{
                    width: 96,
                    height: 64,
                    borderWidth: 1,
                    borderRadius: 6,
                    borderColor: theme.colors.neutral['200'],
                    objectFit: 'contain',
                }}
                src={personDefaultPicture}
            />;
        }

        const url = occurrence.details?.imageUrl;
        if (!url) {
            return undefined;
        }

        const hash = getHashKeyForImage(url);
        if (!images[hash] || !images[hash].blobUrl || images[hash].blobUrl === '') {
            return undefined;
        }

        if (images[hash].platePosition) {
            return <PlateImage
                src={images[hash].blobUrl}
                style={{
                    width: 96,
                    height: 64,
                    borderWidth: 1,
                    borderRadius: 6,
                    borderColor: theme.colors.neutral['200']
                }}
                viewStyle={{
                    alignSelf: 'center',
                }}
                platePosition={images[hash].platePosition}
                width={96}
                height={64}
            />;
        } else {
            return <MyImage
                imageUrl={images[hash].blobUrl}
                style={{
                    width: 96,
                    height: 64,
                    borderWidth: 1,
                    borderRadius: 6,
                    borderColor: theme.colors.neutral['200']
                }}
                viewStyle={{
                    alignSelf: 'center',
                }}
            />;
        }
    }

    return <Container>
        <Header
            title={translate('events')}
            actions={<Filters
                authenticatedUser={authenticatedUser}
                filters={filters}
                setFilters={setFilters}
            />}
        />
        <Kanban
            columns={[{
                key: 'pending',
                label: translate('pending'),
            }, {
                key: 'in_service',
                label: translate('in_service'),
            }, {
                key: 'ongoing',
                label: translate('ongoing'),
            }, {
                key: 'concluded',
                label: translate('concluded'),
            }]}
            cards={(data?.responseList ?? []).map(o => {
                return {
                    column: o.situation,
                    content: o,
                    key: String(o.id),
                };
            })}
            cardContent={(item) => ({
                id: item.content.id,
                start: item.content.createdAt,
                finish: item.content.finishedAt,
                title: item.content.triggerType ? translate(item.content.triggerType) : item.content.occurrenceType.name,
                subTitle: item.content.mapPolygon && { title: translate('area'), content: item.content.mapPolygon.name },
                image: getImage(item.content),
                responsible: item.content.responsibleUser,
                priority: item.content.priority ? item.content.priority === 'maximum' ? 'critical' : item.content.priority : undefined,
                additionalInfo: [
                    ...(item.content.camera ? [item.content.camera.title] : []),
                    ...(item.content.triggerType === 'mp_face_detected' && item.content.details?.mpSituation ? [item.content.details.mpSituation] : []),
                    ...((
                        item.content.triggerType === 'mp_plate_detected' ||
                        item.content.triggerType == 'mp_mobile_agent_plate_detected' ||
                        item.content.triggerType == 'cortex_plate_detected'
                    ) && item.content.details?.mpNatureOfOccurrence ? [item.content.details.mpNatureOfOccurrence] : []),
                ],
                counter: item.content.occurrenceImageCount > 1 ? item.content.occurrenceImageCount :
                    item.content.updatedOccurrencePosition > 1 ? item.content.updatedOccurrencePosition :
                        undefined,
                tags: item.content.tags,
            })}
            onViewableItemsChanged={flatListCallback}
            onClickCard={({ content: item }) => {
                goToEvent(item);
            }}
        />
    </Container>;
}
